/**
 * Saved Users Service
 *
 * Domain layer service for the saved-users ("Favourite people") feature.
 * Reads the local SavedUser records from encrypted storage and returns
 * them sorted newest-first.
 *
 * Singleton pattern for consistent state and efficient resource usage —
 * mirrors SavedContentService and the other domain service singletons.
 *
 * Flutter parity:
 * - lib/presentation/screens/user/saved_users_screen.dart:75-85
 *   (getSavedUsers: read every SavedUser from the encrypted Hive box and
 *    sort descending by createdDate — Flutter's
 *    `a.createdDate < b.createdDate ? 1 : 0` pushes older entries down,
 *    which is effectively newest-first).
 *
 * Differences from SavedContentService:
 * - Unlike SavedContent, there is no `hiddenContent` (or any other)
 *   per-user filter to apply against the local snapshot — the items are
 *   just `{ firebaseId, createdDate }`. The service therefore does not
 *   take a `currentUser` parameter; the blocked-user filter is applied
 *   later by `userService.fetchUsersByIds` after the Firestore round-trip.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { SavedUserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';

export class SavedUsersService {
  private static instance: SavedUsersService;

  private constructor() { }

  public static getInstance(): SavedUsersService {
    if (!SavedUsersService.instance) {
      SavedUsersService.instance = new SavedUsersService();
    }
    return SavedUsersService.instance;
  }

  /**
   * Read all locally-stored SavedUser records, sorted newest first.
   *
   * Sort: descending by `createdDate` — Flutter line 81's
   * `a.createdDate < b.createdDate ? 1 : 0` pushes older entries down,
   * which is effectively newest-first.
   *
   * Mirrors Flutter's getSavedUsers in
   * lib/presentation/screens/user/saved_users_screen.dart:75-85.
   */
  async listLocalSavedUsers(): Promise<SavedUserStorage[]> {
    const storage = SecureStorageWebCrypto.getInstance();
    const items = await storage.getAllWithPrefix<SavedUserStorage>(
      SECURE_STORAGE_KEYS.SAVED_USERS,
    );

    return items.sort((a, b) => b.createdDate - a.createdDate);
  }
}

// Export singleton instance for convenience
export const savedUsersService = SavedUsersService.getInstance();
